import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, Eye, Database, Lock, Users, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: "introduction",
      title: "1. Introdução",
      icon: <Eye className="h-6 w-6 text-[#00B6F0]" />,
      content: `A Arena Pro Player valoriza e respeita sua privacidade. Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos suas informações pessoais quando você utiliza nossa plataforma.

Esta política se aplica a todos os usuários da plataforma Arena Pro Player e está em conformidade com a Lei Geral de Proteção de Dados (LGPD) do Brasil.

Ao utilizar nossa plataforma, você concorda com as práticas descritas nesta política.`
    },
    {
      id: "data-collection",
      title: "2. Informações que Coletamos",
      icon: <Database className="h-6 w-6 text-[#00B6F0]" />,
      content: `Coletamos as seguintes categorias de informações:

📋 Informações de Cadastro:
• Nome completo e nome de usuário
• Endereço de e-mail
• Data de nascimento
• Informações de perfil de jogador

🎮 Informações de Gaming:
• Jogos favoritos e especializações
• Rank/nível nos jogos
• Histórico de mentorias
• Avaliações e feedback

💳 Informações de Pagamento:
• Dados processados por nossos parceiros seguros
• Histórico de transações
• Informações de faturamento (quando aplicável)

📊 Dados de Uso:
• Como você interage com a plataforma
• Preferências e configurações
• Logs de acesso e atividade`
    },
    {
      id: "data-usage",
      title: "3. Como Usamos suas Informações",
      icon: <Users className="h-6 w-6 text-[#00B6F0]" />,
      content: `Utilizamos suas informações para:

🎯 Prestação de Serviços:
• Conectar jogadores com mentores adequados
• Facilitar comunicação e agendamentos
• Processar pagamentos de forma segura
• Personalizar sua experiência na plataforma

📈 Melhoria da Plataforma:
• Analisar padrões de uso para melhorias
• Desenvolver novos recursos
• Otimizar match-making entre usuários

🔒 Segurança e Conformidade:
• Prevenir fraudes e atividades maliciosas
• Cumprir obrigações legais
• Resolver disputas entre usuários

📧 Comunicação:
• Enviar notificações importantes
• Informar sobre atualizações da plataforma
• Marketing (apenas com seu consentimento)`
    },
    {
      id: "data-sharing",
      title: "4. Compartilhamento de Dados",
      icon: <Shield className="h-6 w-6 text-[#00B6F0]" />,
      content: `Seus dados podem ser compartilhados nas seguintes situações:

👥 Entre Usuários:
• Informações de perfil público (nome, especializações, avaliações)
• Informações necessárias para mentorias (conforme autorização)

🤝 Parceiros de Pagamento:
• Dados necessários para processar transações
• Informações limitadas e criptografadas

⚖️ Conformidade Legal:
• Quando exigido por lei ou autoridades competentes
• Para proteger direitos, propriedade ou segurança

📊 Prestadores de Serviços:
• Empresas que nos auxiliam na operação da plataforma
• Apenas dados necessários, com contratos de confidencialidade

❌ Nunca vendemos seus dados pessoais para terceiros para fins de marketing.`
    },
    {
      id: "data-security",
      title: "5. Segurança dos Dados",
      icon: <Lock className="h-6 w-6 text-[#00B6F0]" />,
      content: `Implementamos medidas robustas de segurança:

🔐 Proteção Técnica:
• Criptografia de dados em trânsito e repouso
• Autenticação de dois fatores disponível
• Monitoramento contínuo de segurança
• Backups seguros e regulares

👨‍💻 Controles de Acesso:
• Acesso limitado por necessidade de função
• Logs de auditoria de todos os acessos
• Treinamento de segurança para equipe

🛡️ Políticas de Segurança:
• Revisões regulares de segurança
• Testes de penetração periódicos
• Plano de resposta a incidentes
• Conformidade com padrões da indústria`
    },
    {
      id: "user-rights",
      title: "6. Seus Direitos (LGPD)",
      icon: <Users className="h-6 w-6 text-[#00B6F0]" />,
      content: `De acordo com a LGPD, você tem os seguintes direitos:

📋 Acesso: Solicitar quais dados pessoais possuímos sobre você

✏️ Correção: Solicitar correção de dados incorretos ou incompletos

🗑️ Exclusão: Solicitar exclusão de seus dados pessoais

📦 Portabilidade: Solicitar seus dados em formato estruturado

⏸️ Revogação: Retirar consentimento a qualquer momento

🚫 Oposição: Se opor ao tratamento em certas circunstâncias

Para exercer seus direitos, entre em contato através dos canais disponíveis na seção de contato.`
    },
    {
      id: "cookies",
      title: "7. Cookies e Tecnologias Similares",
      icon: <Database className="h-6 w-6 text-[#00B6F0]" />,
      content: `Utilizamos cookies e tecnologias similares para:

🍪 Tipos de Cookies:
• Essenciais: Necessários para funcionamento básico
• Funcionais: Melhoram sua experiência de uso
• Analíticos: Nos ajudam a entender como a plataforma é usada
• Marketing: Personalizam conteúdo e publicidade (com consentimento)

⚙️ Controle de Cookies:
• Você pode gerenciar cookies através das configurações do navegador
• Alguns cookies são essenciais e não podem ser desabilitados
• A desabilitação pode afetar a funcionalidade da plataforma`
    },
    {
      id: "retention",
      title: "8. Retenção de Dados",
      icon: <Database className="h-6 w-6 text-[#00B6F0]" />,
      content: `Mantemos seus dados pelo tempo necessário para:

⏰ Períodos de Retenção:
• Dados de conta: Enquanto sua conta estiver ativa
• Dados de transação: 5 anos (exigência fiscal)
• Dados de comunicação: 2 anos
• Dados analíticos: 3 anos (anonimizados)

🗑️ Exclusão de Dados:
• Exclusão automática após períodos de retenção
• Exclusão imediata mediante solicitação (quando possível)
• Alguns dados podem ser mantidos para conformidade legal`
    },
    {
      id: "contact",
      title: "9. Contato - Proteção de Dados",
      icon: <Mail className="h-6 w-6 text-[#00B6F0]" />,
      content: `Para questões sobre privacidade e proteção de dados:

📞 Canais de Contato:
• E-mail: privacidade@arenaproplayer.com
• WhatsApp: (11) 99642-5204
• Formulário na plataforma

🏢 Encarregado de Dados (DPO):
• Responsável por questões de privacidade
• Disponível para esclarecer dúvidas sobre LGPD
• Atendimento em horário comercial

📋 Ao entrar em contato:
• Descreva claramente sua solicitação
• Inclua informações para verificação de identidade
• Responderemos em até 15 dias úteis`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1117] via-[#0f141c] to-[#0c1117]">
      {/* Header */}
      <div className="bg-black/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Política de <span className="text-[#00B6F0]">Privacidade</span>
            </h1>
            <p className="text-gray-300 text-lg">
              Última atualização: Janeiro de 2024 | Conforme LGPD
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-8"
        >
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-[#00B6F0]/30 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                {section.icon}
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {section.title}
                </h2>
              </div>
              
              <div className="text-gray-300 leading-relaxed space-y-4">
                {section.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 p-6 bg-[#00B6F0]/10 rounded-xl border border-[#00B6F0]/30"
        >
          <h3 className="text-xl font-bold text-white mb-3">
            🛡️ Seu Direito à Privacidade
          </h3>
          <p className="text-gray-300 leading-relaxed">
            Sua privacidade é fundamental para nós. Esta política é transparente sobre 
            como tratamos seus dados e garante que você tenha controle total sobre suas 
            informações pessoais. Estamos sempre disponíveis para esclarecer dúvidas 
            sobre privacidade e proteção de dados.
          </p>
        </motion.div>
      </div>
    </div>
  );
}; 